// Public MCP directory (GET /directory/mcp). Display only: Symone never fetches these
// URLs, and neither does this page. Vendor links open the vendor's own docs page.
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { qk } from '../api/queries'
import type { McpDirectoryEntry, McpDirectoryResponse } from '../api/types'
import { CopyButton } from '../components/CopyButton'
import { Icon } from '../components/Icon'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'

const AUTH_LABEL: Record<McpDirectoryEntry['auth_kind'], string> = {
  none: 'No auth',
  api_key: 'API key',
  oauth: 'OAuth',
}

const ACRONYMS = new Set(['ai', 'api', 'crm', 'erp', 'hr', 'seo', 'sms', 'ui'])

/**
 * Directory `category` is a free display slug (e.g. `seo`, `project_management`), not the
 * tool-category vocabulary. Shown humanised: `project_management` → "Project management".
 */
function directoryCategoryLabel(slug: string): string {
  return slug
    .split('_')
    .filter(Boolean)
    .map((w, i) => (ACRONYMS.has(w) ? w.toUpperCase() : i === 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

const UNCATEGORISED = '__none__'

/** Only http(s) links are rendered as links. */
function safeHref(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null
  } catch {
    return null
  }
}

export function DirectoryView() {
  const [q, setQ] = useState('')
  const [auth, setAuth] = useState<'all' | McpDirectoryEntry['auth_kind']>('all')
  const [category, setCategory] = useState<string>('all')
  const [officialOnly, setOfficialOnly] = useState(false)
  const dir = useQuery({
    queryKey: qk.mcpDirectory,
    queryFn: () => apiFetch<McpDirectoryResponse>('/directory/mcp'),
    staleTime: 5 * 60_000,
  })
  const entries = useMemo(() => dir.data?.entries ?? [], [dir.data])
  const categories = useMemo(
    () => [...new Set(entries.map((e) => e.category).filter((c): c is string => !!c))].sort(),
    [entries],
  )
  const hasUncategorised = entries.some((e) => !e.category)
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return entries
      .filter(
        (e) =>
          (auth === 'all' || e.auth_kind === auth) &&
          (category === 'all' || (category === UNCATEGORISED ? !e.category : e.category === category)) &&
          (!officialOnly || e.official) &&
          (!needle ||
            e.name.toLowerCase().includes(needle) ||
            (e.category ?? '').toLowerCase().includes(needle) ||
            (e.category ? directoryCategoryLabel(e.category).toLowerCase().includes(needle) : false)),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [entries, q, auth, category, officialOnly])

  return (
    <div className="page">
      <PageHeader
        title="MCP Directory"
        count={dir.data ? entries.length : undefined}
        description="Vendor MCP servers you can add to your own MCP client. Ones marked “In Symone” can also be connected in Toolkits and governed by Symone."
      />
      <QueryState isLoading={dir.isLoading} error={dir.error} what="the MCP directory" onRetry={() => dir.refetch()}>
        {entries.length === 0 ? (
          <EmptyState
            icon="book"
            title="The directory is empty"
            body="Vendor MCP servers appear here once a platform admin publishes them."
            sampleHeaders={['Name', 'Category', 'Auth', 'Official']}
            sampleRows={[['Vendor X', 'CRM', <Badge>OAuth</Badge>, <Badge tone="success">Official</Badge>]]}
          />
        ) : (
          <>
            <div className="dir-toolbar">
              <div className="input-icon">
                <Icon name="search" size={14} />
                <input type="search" aria-label="Search directory" placeholder="Search by name or category" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <label className="chip-select">
                <span className="sr-only">Auth</span>
                <select aria-label="Auth" value={auth} onChange={(e) => setAuth(e.target.value as typeof auth)}>
                  <option value="all">Any auth</option>
                  <option value="none">No auth</option>
                  <option value="api_key">API key</option>
                  <option value="oauth">OAuth</option>
                </select>
              </label>
              <label className="chip-select">
                <span className="sr-only">Category</span>
                <select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="all">Any category</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{directoryCategoryLabel(c)}</option>
                  ))}
                  {hasUncategorised && <option value={UNCATEGORISED}>Uncategorised</option>}
                </select>
              </label>
              <label className="check">
                <input type="checkbox" checked={officialOnly} onChange={(e) => setOfficialOnly(e.target.checked)} />
                Official only
              </label>
            </div>
            <div className="table-wrap">
              {visible.length === 0 ? (
                <div className="state-panel state-panel--quiet"><p>No directory entries match.</p></div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Auth</th>
                      <th>Official</th>
                      <th>MCP URL</th>
                      <th><span className="sr-only">Links</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((e) => {
                      const docs = safeHref(e.vendor_url)
                      return (
                        <tr key={e.slug}>
                          <td>
                            <div className="cell-strong">
                              {e.name}
                              {e.connector_name && (
                                <span title={e.connector_auth_kind ? `Connect in Toolkits with ${AUTH_LABEL[e.connector_auth_kind]}` : undefined}>
                                  <Badge tone="info">In Symone</Badge>
                                </span>
                              )}
                            </div>
                            {e.setup_steps_md && (
                              <details>
                                <summary className="cell-sub">Setup steps</summary>
                                {/* Admin-written markdown, rendered as plain text (never as HTML). */}
                                <pre className="dir-steps">{e.setup_steps_md}</pre>
                              </details>
                            )}
                          </td>
                          <td>
                            {e.category ? (
                              <button type="button" className="cat-chip" title={`Show only ${directoryCategoryLabel(e.category)}`} onClick={() => setCategory(e.category ?? 'all')}>
                                {directoryCategoryLabel(e.category)}
                              </button>
                            ) : (
                              <span className="muted">Uncategorised</span>
                            )}
                          </td>
                          <td><Badge>{AUTH_LABEL[e.auth_kind] ?? e.auth_kind}</Badge></td>
                          <td>
                            {e.official ? (
                              <Badge tone="success">Official</Badge>
                            ) : (
                              <span className="muted" title="Not run by the vendor itself">Third-party</span>
                            )}
                          </td>
                          <td>
                            <div className="badge-row">
                              <code>{e.mcp_url}</code>
                              <CopyButton value={e.mcp_url} label="Copy" />
                            </div>
                          </td>
                          <td className="cell-actions nowrap">
                            {docs && (
                              <a className="link-btn" href={docs} target="_blank" rel="noopener noreferrer" aria-label={`${e.name} docs`}>
                                Docs <Icon name="external" size={12} />
                              </a>
                            )}
                            {e.connector_name && (
                              <Link className="link-btn" to={`/toolkits?search=${encodeURIComponent(e.connector_name)}`}>
                                Toolkit <Icon name="chevron" size={12} />
                              </Link>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </QueryState>
    </div>
  )
}
